import { Form, Formik } from "formik";
import { number, object } from "yup";
import { styled } from "@/components/content/stitches";
import { Box } from "@/components/ui/box";
import { Stack } from "@/components/ui/stack";
import { Input } from "@/components/uielements/formik";
import { Modal } from "@/components/uielements/modal";
import { StyledPrimaryButton } from "@/components/uielements/primaryButton/styles";
import { toDollars } from "@/utils/basket";
import { capitalizeFirstLetter } from "@/utils/stringHelpers";

/**
 * @typedef Props
 * @property {number} initialAmount - The amount of banked currency in **cents** available to wether redeem or donate
 * @property {"redeem" | "donate"} rewardActionType - The type of action to perform with the banked currency
 * @property {(amount: number) => void} onSubmit
 * @property {boolean} isLoading - Whether the modal is loading
 */
interface Props {
  initialAmount: number;
  rewardActionType: "redeem" | "donate";
  onSubmit: (amount: number) => void;
  isLoading: boolean;
}

export function BankedCurrencyModal({
  initialAmount,
  rewardActionType,
  onSubmit,
  isLoading,
}: Props) {
  const amount = toDollars(initialAmount);
  const rewardAction =
    rewardActionType === "redeem" ? "Redemption" : "Donation";
  const amountSchema = object({
    amount: number()
      .required()
      .max(
        Number(amount),
        `Please enter an amount up to $${toDollars(initialAmount)}`
      )
      .min(0, `${rewardAction} amount must be more than $0`),
  });

  return (
    <>
      <Modal.Title>{`${capitalizeFirstLetter(
        rewardActionType
      )} Your Reward`}</Modal.Title>

      <Stack
        align="center"
        direction="vertical"
        gap="$4"
        css={{
          minHeight: "100%",
          textAlign: "center",
        }}
      >
        <>
          <Formik
            validateOnBlur
            validateOnChange
            initialValues={{ amount: toDollars(initialAmount) }}
            onSubmit={({ amount }, { resetForm }) => {
              // Convert amount to cents
              onSubmit(Number(amount) * 100);
              resetForm();
            }}
            validationSchema={amountSchema}
          >
            {({ isValid }) => (
              <Form>
                <Content>
                  <Description>
                    {`You have $${toDollars(
                      initialAmount
                    )} available to ${rewardActionType}.`}
                  </Description>

                  <Box
                    css={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i
                      style={{
                        position: "relative",
                        left: "1rem",
                        fontStyle: "normal",
                      }}
                    >
                      $
                    </i>
                    <Input
                      style={{ paddingLeft: "1.125rem" }}
                      placeholder="0.00"
                      pattern="[0-9.]*"
                      type="number"
                      step="0.01"
                      autoFocus
                      id="amount"
                      name="amount"
                      min="0"
                      label={`Enter amount of rewards available to ${rewardActionType}`}
                    />
                  </Box>
                </Content>

                <Modal.Footer>
                  <Modal.Cancel>Close</Modal.Cancel>

                  <StyledPrimaryButton
                    disabled={!isValid || isLoading}
                    buttonTheme="secondary"
                    type="submit"
                  >
                    {capitalizeFirstLetter(rewardActionType)}
                  </StyledPrimaryButton>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </>
      </Stack>
    </>
  );
}

const Description = styled("p", {
  fontSize: "1rem",
  margin: 0,
});

const Content = styled(Modal.Content, {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  position: "relative",
  overflowY: "auto",
});
